/**
 * scenario.ts - build an initial GameState for the v1 new game flow.
 *
 * Minimal starter scenario:
 *  - Player empire (RACE-AA Terran Consortium) + two AI empires.
 *  - Small galaxy of ~12 star systems with 1-3 planets each.
 *  - Each empire starts with one colonised Gaia world and a small ship
 *    group (Class-1 ships drawn from its race's roster in ships.json).
 *    Ships are the unit of sim - there is no Fleet wrapper in v1.
 *
 * All randomness runs through GameState.rng so reloading with the same seed
 * produces identical starting conditions.
 */

import type { LoadedGameData } from '../data/loader';
import { getRaceName } from '../data/loader';
import { wireJumpGraph } from './jump-graph';
import { createRng, nextFloat, nextInt } from '../state/rng';
import { resolveSlotWeapon } from './combat-balance';
import { effectiveMaxHull, effectiveMaxShields } from './research-effects';
import { maxHullForLevel, maxStructuresForSize } from './balance-structures';
import {
  SAVE_FORMAT_VERSION,
  allocEntityId,
  EMPIRE_NEUTRAL_ID,
  type AiBehaviour,
  type AiDifficulty,
  type BiomeKind,
  type Empire,
  type EnvironmentKind,
  type GameState,
  type JumpPoint,
  type Planet,
  type RaceId,
  type ShipInstance,
  type StarSystem,
  type Structure,
  type StructureKind,
  type SystemId,
  type WeaponSlotState,
} from '../state/types';

/** A single AI-opponent slot in NewGameOptions. */
export interface AiOpponentSpec {
  /** Specific race, or 'random' to pick from v1 races via the seeded RNG. */
  raceId: RaceId | 'random';
  /** v3M7.1 — AI difficulty tier (defaults Normal). */
  difficulty?: AiDifficulty;
  /** v3M7.1 — AI behaviour archetype (defaults Balanced). */
  behaviour?: AiBehaviour;
}

export interface NewGameOptions {
  seed: number;
  /**
   * Player race — any v1-status race. Multi-empire same-race is allowed
   * per UI_UX §9.2, so the player pick does NOT exclude races from AI slots.
   */
  playerRace?: RaceId;
  systemCount?: number;
  /**
   * AI opponent slots (v2M1). One empire per entry. Each slot picks any v1
   * race independently — duplicates with each other and with the player are
   * legal. Slots resolved to 'random' use the seeded RNG so the full new-game
   * state remains reproducible from (seed, settings).
   *
   * Undefined keeps the legacy v1 behaviour: one AI per remaining race,
   * spawned in RACE-AA / BB / CC order minus the player's pick.
   */
  aiOpponents?: readonly AiOpponentSpec[];
  /**
   * v3M21.1 — fraction of non-homeworld habitable planets seeded as
   * populated neutral planets (`DIPLOMACY.md §3`). Range 0–0.6
   * (UI_UX §9.2 caps at 60%); the v3M24.3 slider exposes this in
   * 5% steps. Undefined falls back to
   * `state.balanceData.diplomacy.neutralPlanetDensity` (default 0.30
   * in Balance.xlsx). 0 = no neutrals seeded.
   */
  neutralPlanetDensity?: number;
}

/** v1-status races that can legally appear in the game. */
const V1_RACES: readonly RaceId[] = ['RACE-AA', 'RACE-BB', 'RACE-CC'];

/**
/**
 * MFix1.4 — planet size lives on a 5–20 numeric range (PLANETS §2.1).
 * `populationCap = size × 10`; min/max chosen so the smallest planet
 * (cap 50) and largest (cap 200) bracket the legacy small/medium/large
 * caps so prior balance intuition still applies. Home worlds lock to
 * homePlanetSize for both gameplay parity (predictable starting cap)
 * and visual parity (home worlds always look big-ish).
 *
 * v3M10.1: PLANET_SIZE_MIN/MAX/HOME_PLANET_SIZE moved to state.balanceData.economy.
 */
const POP_CAP_PER_SIZE = 10;

/**
 * Roll a planet size favouring the middle of the [5,20] range. We average
 * two uniform rolls (triangular distribution) so the most common outcome
 * lands near 12-13 with the extremes (5, 20) rare. Cheaper than a true
 * normal sample and good enough for v1 distribution.
 *
 * v3M10.1: PLANET_SIZE_MIN/MAX/HOME_PLANET_SIZE now in state.balanceData.economy.
 */
function rollPlanetSize(state: GameState): number {
  const span = state.balanceData.economy.planetSizeMax - state.balanceData.economy.planetSizeMin;
  const a = nextFloat(state.rng);
  const b = nextFloat(state.rng);
  const t = (a + b) / 2; // triangular on [0,1] peaked at 0.5
  return state.balanceData.economy.planetSizeMin + Math.round(t * span);
}

/**
 * MFix4.1 — Balanced biome distribution per PLANETS §2.6 default. Gaia
 * is rare (5%) per §2.2 "the prize everyone fights over"; the four
 * habitable biomes (Temperate / Desert / Ocean / Arctic) are roughly
 * equal at ~18% each; Barren and Volcanic round out the hostile end.
 *
 * Weights are absolute (sum 100). A future game-setup screen will offer
 * preset distributions (Hostile-leaning / Habitable-leaning) plus a
 * custom-weights mode — this constant is the v1 default.
 */
const BALANCED_BIOME_WEIGHTS: ReadonlyArray<{ biome: BiomeKind; weight: number }> =
  Object.freeze([
    { biome: 'gaia', weight: 5 },
    { biome: 'temperate', weight: 18 },
    { biome: 'desert', weight: 18 },
    { biome: 'ocean', weight: 18 },
    { biome: 'arctic', weight: 18 },
    { biome: 'barren', weight: 12 },
    { biome: 'volcanic', weight: 11 },
  ]);

const BALANCED_BIOME_TOTAL_WEIGHT = BALANCED_BIOME_WEIGHTS.reduce(
  (sum, e) => sum + e.weight,
  0,
);

/**
 * v3M20 — pick a flavour name from `pool` without replacement, tracked
 * via the per-galaxy `used` Set. Sampling uses a single nextFloat draw
 * to preserve the determinism-by-seed contract.
 *
 * Pool exhaustion: when every entry in `pool` is already in `used`,
 * fall back to "<base> II" / "<base> III" suffixes — the base name
 * comes from a fresh nextFloat draw on the full pool, so the choice
 * is also seed-deterministic.
 *
 * Used by `pickSystemName` / `pickPlanetName` in `createInitialGameState`.
 */
function pickFromPool(
  state: GameState,
  pool: readonly string[],
  used: Set<string>,
): string {
  if (pool.length === 0) {
    // Defensive — should never happen on a real catalogue but keeps
    // the helper total. Caller can substitute a placeholder if needed.
    return '';
  }
  // Common path: at least one name still unused. Pick uniformly from
  // the unused subset so the empty pool branch only runs once per
  // galaxy worth of picks.
  if (used.size < pool.length) {
    // Build the unused subset once. Pool sizes are small (≤200), so
    // the per-call O(N) filter is negligible.
    const available: string[] = [];
    for (const n of pool) {
      if (!used.has(n)) available.push(n);
    }
    const idx = Math.floor(nextFloat(state.rng) * available.length);
    const pick = available[idx]!;
    used.add(pick);
    return pick;
  }
  // Pool exhausted — pick a base from the full pool, append the next
  // available roman-numeral suffix. Caps at L/M+ to keep the helper
  // total even on adversarial inputs (galaxy-size > pool × 50 etc.).
  const baseIdx = Math.floor(nextFloat(state.rng) * pool.length);
  const base = pool[baseIdx]!;
  for (let n = 2; n < 1000; n++) {
    const candidate = `${base} ${toRoman(n)}`;
    if (!used.has(candidate)) {
      used.add(candidate);
      return candidate;
    }
  }
  // Adversarial fallback — base + entity-id-style suffix. Should never
  // happen in v1 (max galaxy size × planets is far below the cap).
  const candidate = `${base} ${used.size}`;
  used.add(candidate);
  return candidate;
}

/**
 * v3M20 — toRoman converts 2..999 to Roman-numeral strings (II..CMXCIX).
 * Only used by `pickFromPool` for pool-exhaustion suffixes; `1` is
 * suppressed since the un-suffixed base name covers that slot.
 */
function toRoman(n: number): string {
  const map: Array<[number, string]> = [
    [900, 'CM'], [500, 'D'], [400, 'CD'], [100, 'C'],
    [90, 'XC'], [50, 'L'], [40, 'XL'], [10, 'X'],
    [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I'],
  ];
  let out = '';
  let rem = n;
  for (const [value, glyph] of map) {
    while (rem >= value) {
      out += glyph;
      rem -= value;
    }
  }
  return out;
}

/**
 * Pick a biome by the Balanced weighted distribution. Single nextFloat
 * call so the determinism-by-seed contract is preserved across seeded
 * scenarios. Used for non-home planets — home worlds are locked to Gaia.
 */
function rollBiome(state: GameState): BiomeKind {
  const r = nextFloat(state.rng) * BALANCED_BIOME_TOTAL_WEIGHT;
  let acc = 0;
  for (const entry of BALANCED_BIOME_WEIGHTS) {
    acc += entry.weight;
    if (r < acc) return entry.biome;
  }
  // Defensive fallback — floating-point edge case. Last entry by design.
  return BALANCED_BIOME_WEIGHTS[BALANCED_BIOME_WEIGHTS.length - 1]!.biome;
}

export function createInitialGameState(
  data: LoadedGameData,
  opts: NewGameOptions,
): GameState {
  const state: GameState = {
    version: SAVE_FORMAT_VERSION,
    tick: 0,
    rng: createRng(opts.seed),
    // v3M21.1 — start at 0 so the very first allocEntityId() call (the
    // synthetic neutral empire injection below) lands exactly at
    // EMPIRE_NEUTRAL_ID = 0. Player empire is then allocated id 1, AI
    // empires id 2..N. Older callers using `nextEntityId: 1` in test
    // fixtures keep working — they don't inject a neutral empire and
    // therefore have no need to reserve id 0.
    nextEntityId: 0,
    empires: new Map(),
    planets: new Map(),
    systems: new Map(),
    jumpPoints: new Map(),
    warpGateLinks: new Map(),
    ships: new Map(),
    missiles: new Map(),
    structures: new Map(),
    environment: new Map(),
    shipTemplates: data.shipTemplates,
    auras: data.auras,
    weapons: data.weaponDefs,
    techs: data.techDefs,
    habitability: data.habitability,
    aiBehaviours: data.aiBehaviours,
    races: data.races,
    structuresData: data.structures,
    balanceData: data.balance,
    teamVision: new Map(),
    combatEvents: [],
    playerEmpireId: 0,
    gameOutcome: 'in-progress',
  };

  const playerRace: RaceId = opts.playerRace ?? 'RACE-AA';
  // Resolve the AI opponent list. New path (v2M1 + v3M7.1): honour
  // opts.aiOpponents exactly, resolving 'random' via the seeded RNG and
  // carrying difficulty + behaviour through. Legacy path (undefined):
  // one AI per v1 race other than the player's pick, all on
  // Normal / Balanced.
  interface ResolvedAiSlot {
    raceId: RaceId;
    difficulty: AiDifficulty;
    behaviour: AiBehaviour;
  }
  const aiSlots: ResolvedAiSlot[] = opts.aiOpponents
    ? opts.aiOpponents.map((slot) => ({
        raceId:
          slot.raceId === 'random'
            ? V1_RACES[nextInt(state.rng, 0, V1_RACES.length - 1)]!
            : slot.raceId,
        difficulty: slot.difficulty ?? 'Normal',
        behaviour: slot.behaviour ?? 'Balanced',
      }))
    : V1_RACES.filter((r) => r !== playerRace).map((raceId) => ({
        raceId,
        difficulty: 'Normal' as AiDifficulty,
        behaviour: 'Balanced' as AiBehaviour,
      }));
  // v3M21.1 — synthetic neutral empire (EMPIRE_NEUTRAL_ID = 0). Owns
  // every populated-but-unaffiliated planet seeded later in this
  // function, plus their pre-built defensive structures. AI-inert: no
  // dispatch, no research, no build. Iteration sites that walk
  // `state.empires` for AI dispatch / scoreboard / turn order /
  // victory checks must filter via the EMPIRE_NEUTRAL_ID guard.
  // Allocated FIRST so the first allocEntityId() returns 0; assertion
  // inside makeNeutralEmpire guards the invariant.
  makeNeutralEmpire(state);

  // Empire colours: player cyan, AI cycle through an orange/magenta/green/
  // teal/yellow palette by spawn order. Multi-empire same-race is legal
  // (v2M1) so colour is the primary visual differentiator on the map.
  //
  // Empire names resolve from data.races (Ships.xlsx → Races sheet) via
  // getRaceName so spreadsheet renames propagate without code churn.
  const playerEmpire = makeEmpire(
    state,
    getRaceName(data, playerRace),
    playerRace,
    true,
    '#4aa0ff',
  );
  state.playerEmpireId = playerEmpire.id;
  const aiColors = ['#ff9240', '#d84cc4', '#63c77c', '#4fe0d6', '#f0d94a'];
  const aiEmpires = aiSlots.map((slot, i) =>
    makeEmpire(
      state,
      `${getRaceName(data, slot.raceId)} AI ${i + 1}`,
      slot.raceId,
      false,
      aiColors[i % aiColors.length]!,
      slot.difficulty,
      slot.behaviour,
    ),
  );
  const allEmpires = [playerEmpire, ...aiEmpires];

  // v3M21.1 — every empire in state.empires (including the synthetic
  // neutral) gets a teamVision entry so lookups never return undefined.
  // The neutral's vision map stays empty for the lifetime of the game
  // (it scans nothing, owns no ships) but the entry's presence is
  // load-bearing for any future code that does `state.teamVision.get(id)`
  // without a null check.
  for (const empire of state.empires.values()) {
    state.teamVision.set(empire.id, new Map());
  }

  // v3M20 — name pools sourced from data.names, sampled without
  // replacement via the per-galaxy `used` Sets. Each pickFromPool call
  // consumes one nextFloat draw so the placement is seed-deterministic.
  const usedSystemNames = new Set<string>();
  const usedPlanetNames = new Set<string>();

  const systemCount = opts.systemCount ?? 12;
  const systems: StarSystem[] = [];
  for (let i = 0; i < systemCount; i++) {
    // MFix6.6 fix-up — keep new systems at least MIN_SYSTEM_DISTANCE
    // apart in galaxy coords. Pure-random placement could land two
    // stars on top of each other; the bigger sprites from MFix6.6
    // make any overlap visually broken. Retry up to MAX_PLACEMENT_RETRIES;
    // if every retry collides (overpacked galaxy / unlucky seed), accept
    // the last candidate so we never deadlock generation.
    const { x, y } = pickSystemPosition(state, systems);
    const sys: StarSystem = {
      id: allocEntityId(state),
      name: pickFromPool(state, data.names.systems, usedSystemNames),
      x,
      y,
      planetIds: [],
      jumpPointIds: [],
    };
    state.systems.set(sys.id, sys);
    systems.push(sys);

    const pCount = 1 + nextInt(state.rng, 0, 3);
    for (let p = 0; p < pCount; p++) {
      // MFix1.4 — non-home planets get a middling-favoured random size.
      // MFix4.1 — biome rolled by the Balanced weighted distribution per
      // PLANETS §2.6 (replaces the v1 hardcoded 'barren' shortcut).
      const planet = makePlanet(
        state,
        sys,
        null,
        pickFromPool(state, data.names.planets, usedPlanetNames),
        rollBiome(state),
        rollPlanetSize(state),
      );
      sys.planetIds.push(planet.id);
    }

    populateJumpPoints(state, sys);
    populateEnvironment(state, sys);
  }

  wireJumpGraph(state);

  for (let e = 0; e < allEmpires.length; e++) {
    const empire = allEmpires[e]!;
    const sys = systems[e * Math.floor(systems.length / allEmpires.length)]!;
    // MFix1.4 — home worlds locked to homePlanetSize (15). Starting pop
    // 15 is unchanged from prior balance: 10% of the 150-cap home world.
    const home = makePlanet(
      state,
      sys,
      empire.id,
      pickFromPool(state, data.names.planets, usedPlanetNames),
      'gaia',
      state.balanceData.economy.homePlanetSize,
      15,
    );
    sys.planetIds.unshift(home.id);
    empire.planetIds.push(home.id);
    // MFix6.3 — Home system is the empire's first scouted system.
    // Other systems become scouted on jump-arrival in advance.ts.
    empire.scoutedSystemIds.add(sys.id);

    spawnStartingShips(state, empire, sys.id, data);
  }

  // v3M21.1 — seed populated neutral planets (`DIPLOMACY.md §3`).
  // Walks every still-uninhabited planet (ownerEmpireId === null)
  // and, for each on an eligible biome, rolls against `density` to
  // claim it for the synthetic neutral empire. Eligibility derives
  // from `state.balanceData.diplomacy.defensiveStructures`: any biome
  // with zero structures across all categories is treated as
  // uninhabitable for established neutral civilisations and skipped.
  // Each seeded planet carries a randomised starting pop in
  // [neutralStartingPopMin, neutralStartingPopMax] × populationCap;
  // Loyalty is already 100 from makePlanet so bombardment-to-Annex
  // works the same as any other owned planet (PLANETS §2.7).
  // Pre-built defensive structures land in v3M21.1.5.
  const density =
    opts.neutralPlanetDensity ?? state.balanceData.diplomacy.neutralPlanetDensity;
  if (density > 0) {
    const neutral = state.empires.get(EMPIRE_NEUTRAL_ID)!;
    const popMin = state.balanceData.diplomacy.neutralStartingPopMin;
    const popMax = state.balanceData.diplomacy.neutralStartingPopMax;
    const defenses = state.balanceData.diplomacy.defensiveStructures;
    for (const planet of state.planets.values()) {
      if (planet.ownerEmpireId !== null) continue;
      const def = defenses[planet.biome];
      const totalDefenses = def.bunkers + def.surfaceBatteries + def.embassy;
      if (totalDefenses === 0) continue;
      if (nextFloat(state.rng) >= density) continue;
      planet.ownerEmpireId = EMPIRE_NEUTRAL_ID;
      const popFraction = popMin + nextFloat(state.rng) * (popMax - popMin);
      planet.currentPopulation = Math.floor(planet.populationCap * popFraction);
      neutral.planetIds.push(planet.id);
      // v3M21.1.5 — pre-build biome-appropriate defensive structures
      // (`DIPLOMACY.md §3` table). Cap-clamps automatically so a small
      // planet doesn't overflow its slot count.
      seedNeutralDefenses(state, planet);
    }
  }

  return state;
}

/**
 * MFix6.6 fix-up — minimum Euclidean distance between any two system
 * star positions in galaxy coords. 0.12 = 12% of the unit-square; 12
 * systems can comfortably distribute themselves with this gap (a
 * uniform 4×3 grid would be 0.25 / 0.33 apart). Higher counts
 * gracefully fall back to closer placement once the retry budget is
 * exhausted — generation never deadlocks on overpacked seeds.
 */
const MIN_SYSTEM_DISTANCE = 0.12;
const MAX_PLACEMENT_RETRIES = 50;

/**
 * Draw a candidate `(x, y)` for a new system, retrying up to
 * `MAX_PLACEMENT_RETRIES` times to satisfy `MIN_SYSTEM_DISTANCE` against
 * every already-placed system. Coords stay inside the 0.05 — 0.95 inset
 * so stars don't render behind the HUD or the right-hand panel. The
 * RNG is the deterministic scenario rng, so the same seed always
 * produces the same layout.
 */
function pickSystemPosition(
  state: GameState,
  placed: ReadonlyArray<{ x: number; y: number }>,
): { x: number; y: number } {
  const minDistSq = MIN_SYSTEM_DISTANCE * MIN_SYSTEM_DISTANCE;
  let candidate = { x: 0.5, y: 0.5 };
  for (let attempt = 0; attempt < MAX_PLACEMENT_RETRIES; attempt += 1) {
    candidate = {
      x: 0.05 + nextFloat(state.rng) * 0.9,
      y: 0.05 + nextFloat(state.rng) * 0.9,
    };
    let ok = true;
    for (const other of placed) {
      const dx = candidate.x - other.x;
      const dy = candidate.y - other.y;
      if (dx * dx + dy * dy < minDistSq) {
        ok = false;
        break;
      }
    }
    if (ok) return candidate;
  }
  // Retry budget exhausted — accept the last candidate so a
  // pathologically packed galaxy (e.g. 30+ systems) still finishes
  // generation. Visually it'll have a few overlaps but nothing breaks.
  return candidate;
}

/**
 * Seed environmental entities into a system at deterministic random
 * positions between the inner-orbit ring (r=0.14) and the outer
 * jump-point ring (r=0.48). Guarantees ≥1 asteroid field per system so
 * every empire has a Mining Outpost site in reach; gas giants are
 * rarer. MFix4.2 added the exotic cosmetic kinds (nebula, ion cloud,
 * radiation belt at 0-1 per system; black hole rolled per
 * BLACK_HOLE_PROBABILITY).
 */
function populateEnvironment(state: GameState, sys: StarSystem): void {
  const asteroidCount =
    state.balanceData.scenario.asteroidFieldsMin + nextInt(state.rng, 0, state.balanceData.scenario.asteroidFieldsMax - state.balanceData.scenario.asteroidFieldsMin + 1);
  for (let i = 0; i < asteroidCount; i++) {
    placeEnvironmentEntity(state, sys, 'asteroid-field');
  }
  // MFix5.4 — guarantee >=1 gas giant per system so the Gas Giant Refinery
  // build site exists in every empire's reach.
  const gasGiantCount =
    state.balanceData.scenario.gasGiantsMin + nextInt(state.rng, 0, state.balanceData.scenario.gasGiantsMax - state.balanceData.scenario.gasGiantsMin + 1);
  for (let i = 0; i < gasGiantCount; i++) {
    placeEnvironmentEntity(state, sys, 'gas-giant');
  }
  // MFix4.2 — exotic cosmetic terrain. Each kind rolled independently so
  // a system can host multiple distinct exotic features; counts are
  // deliberately low so the tactical map doesn't feel cluttered.
  const nebulaCount = nextInt(state.rng, 0, state.balanceData.scenario.nebulaeMax + 1);
  for (let i = 0; i < nebulaCount; i++) {
    placeEnvironmentEntity(state, sys, 'nebula');
  }
  const ionCloudCount = nextInt(state.rng, 0, state.balanceData.scenario.ionCloudsMax + 1);
  for (let i = 0; i < ionCloudCount; i++) {
    placeEnvironmentEntity(state, sys, 'ion-cloud');
  }
  const radiationBeltCount = nextInt(state.rng, 0, state.balanceData.scenario.radiationBeltsMax + 1);
  for (let i = 0; i < radiationBeltCount; i++) {
    placeEnvironmentEntity(state, sys, 'radiation-belt');
  }
  if (nextFloat(state.rng) < state.balanceData.scenario.blackHoleProbability) {
    placeEnvironmentEntity(state, sys, 'black-hole');
  }
}

function placeEnvironmentEntity(
  state: GameState,
  sys: StarSystem,
  kind: EnvironmentKind,
): void {
  // Place between r=0.16 (outside innermost orbit) and r=0.44 (inside
  // jump-point ring). Random angle from the scenario RNG.
  const r = 0.16 + nextFloat(state.rng) * 0.28;
  const a = nextFloat(state.rng) * Math.PI * 2;
  const sx = 0.5 + Math.cos(a) * r;
  const sy = 0.5 + Math.sin(a) * r;
  const id = allocEntityId(state);
  state.environment.set(id, {
    id,
    systemId: sys.id,
    sx,
    sy,
    radius: state.balanceData.scenario.environmentEntityRadius,
    kind,
  });
}

/** Generate 3-6 jump points on the outer ring (r = 0.48) with even-ish spacing. */
function populateJumpPoints(state: GameState, sys: StarSystem): void {
  const count = 3 + nextInt(state.rng, 0, 4);
  const baseStep = (Math.PI * 2) / count;
  const startAngle = nextFloat(state.rng) * Math.PI * 2;
  const radius = 0.48;
  for (let k = 0; k < count; k++) {
    const jitter = (nextFloat(state.rng) - 0.5) * baseStep * 0.4;
    const angle = startAngle + k * baseStep + jitter;
    const jp: JumpPoint = {
      id: allocEntityId(state),
      systemId: sys.id,
      x: 0.5 + Math.cos(angle) * radius,
      y: 0.5 + Math.sin(angle) * radius,
      linkedSystemId: null,
      linkedJumpPointId: null,
    };
    state.jumpPoints.set(jp.id, jp);
    sys.jumpPointIds.push(jp.id);
  }
}

function makeEmpire(
  state: GameState,
  name: string,
  raceId: RaceId,
  isPlayer: boolean,
  color: string,
  aiDifficulty: AiDifficulty = 'Normal',
  aiBehaviour: AiBehaviour = 'Balanced',
): Empire {
  // v3M7.1 — apply the AI difficulty's startingResourceBonus to seed
  // resources for non-player empires. Player resources are unchanged by
  // difficulty (the bonus belongs to the AI, not the player). Without
  // this branch a Hard AI would still spawn with 1000/500/100 — the
  // player would never feel the difficulty leap during the early game.
  const startingBonus = isPlayer
    ? 1
    : (state.aiBehaviours.difficulties[aiDifficulty]?.startingResourceBonus ?? 1);
  const empire: Empire = {
    id: allocEntityId(state),
    name,
    raceId,
    isPlayer,
    resources: {
      credits: Math.round(1000 * startingBonus),
      research: 0,
      minerals: Math.round(500 * startingBonus),
      population: 0,
      diplomatic: 0,
      fuel: Math.round(100 * startingBonus),
    },
    planetIds: [],
    // Baseline techs are auto-researched for every starter empire:
    // zero-cost, no prereqs, race matches (or 'All'). This covers every
    // Mk 1 rung of the tech tree — Standard Hull Mk 1, Titanium Plating,
    // Standard Shield, Hyperdrive Efficiency Mk 1, Class 1 Starship
    // Chassis, Mk 1 of each weapon subtype — so the research panel
    // opens with a consistent view: all Mk 1s marked ✓, higher Mks
    // require explicit research. `effectiveMaxHull` / `effectiveArmor`
    // / `hyperdriveCostMultiplier` all defaulted to Mk 1 before and
    // continue to do so — this just aligns the UI state with the
    // baseline behaviour.
    //
    // MFix7.1 — Cloaking is no longer pre-seeded into researchedTechs.
    // The M8 playtest-UX carryover that auto-researched it for every
    // empire is removed; players must research the Cloaking tech before
    // the C-key cloak toggle activates. RACE-CC's Advanced Cloaking
    // stays unresearched as a lategame earn (unchanged).
    researchedTechs: collectBaselineTechs(state, raceId),
    activeResearch: null,
    shipIds: [],
    color,
    aiDifficulty,
    aiBehaviour,
    // MFix6.3 — Empire starts with no scouted systems. Home system is
    // added by the caller (createInitialGameState) once it's been
    // chosen + ships have been spawned there.
    scoutedSystemIds: new Set<SystemId>(),
    // v3M21.1 — empires start with no diplomatic focus; players /
    // AI explicitly set a focus via the `setDiplomaticFocus` command.
    diplomaticFocus: null,
  };
  state.empires.set(empire.id, empire);
  return empire;
}

/**
 * v3M21.1 — Construct the synthetic neutral empire and register it at
 * `EMPIRE_NEUTRAL_ID`. Owns every populated-but-unaffiliated planet
 * seeded later in `createInitialGameState`. AI-inert: race `'Neutral'`,
 * behaviour `'Neutral'`, no research, no homeworld, zero resources, no
 * AI dispatch (every iteration site filters via the EMPIRE_NEUTRAL_ID
 * guard). Difficulty `'Normal'` is a placeholder — the AI dispatcher
 * never reads it for the neutral row.
 *
 * Allocation invariant: `state.nextEntityId` must be `0` when this is
 * called (canonical site: top of `createInitialGameState`, before any
 * `makeEmpire` call). The function asserts `empire.id === EMPIRE_NEUTRAL_ID`
 * and throws if the invariant is violated, so a future code path that
 * accidentally allocates an entity id before this call surfaces
 * loudly rather than silently miswiring the diplomacy system.
 */
function makeNeutralEmpire(state: GameState): Empire {
  const empire: Empire = {
    id: allocEntityId(state),
    name: 'Neutral',
    raceId: 'Neutral',
    isPlayer: false,
    resources: {
      credits: 0,
      research: 0,
      minerals: 0,
      population: 0,
      diplomatic: 0,
      fuel: 0,
    },
    planetIds: [],
    researchedTechs: new Set(),
    activeResearch: null,
    shipIds: [],
    // Neutral grey — distinguishable from the player cyan and the AI
    // colour palette so neutral planets read at a glance on the map.
    color: '#6b7280',
    aiDifficulty: 'Normal',
    aiBehaviour: 'Neutral',
    scoutedSystemIds: new Set<SystemId>(),
    diplomaticFocus: null,
  };
  if (empire.id !== EMPIRE_NEUTRAL_ID) {
    throw new Error(
      `Synthetic neutral empire allocated id ${empire.id} but expected ${EMPIRE_NEUTRAL_ID}; ` +
        `check createInitialGameState ordering (must be the first allocEntityId call after nextEntityId: 0 init)`,
    );
  }
  state.empires.set(empire.id, empire);
  return empire;
}

/**
 * v3M21.1.5 — Pre-build the biome-appropriate defensive structure stack
 * on a freshly-seeded neutral planet (`DIPLOMACY.md §3`). Each entry in
 * `state.balanceData.diplomacy.defensiveStructures[planet.biome]` is the
 * **starting level** of one instance of that structure kind; 0 means
 * "don't build that kind". Structures are inserted at level N with
 * `buildTicksRemaining: 0` (already complete) and full hull, so they
 * activate immediately without needing the v2M5 build pipeline.
 *
 * Slot-cap aware: builds in priority order (Bunker → Surface Battery
 * → Embassy) and stops once `planet.structureIds.length` hits
 * `maxStructuresForSize(planet.size)`. Small planets (size 5) may not
 * fit the full stack; the lower-priority entries silently drop. Levels
 * exceeding a kind's catalogue maximum (e.g. Bunker level > 5, Embassy
 * level > 1) are clamped silently. Every structure is owned by the
 * synthetic neutral empire via the planet's `ownerEmpireId`; the
 * Structure record itself doesn't carry an empire id.
 */
function seedNeutralDefenses(state: GameState, planet: Planet): void {
  const biomeDef = state.balanceData.diplomacy.defensiveStructures[planet.biome];
  if (!biomeDef) return;
  const slotCap = maxStructuresForSize(planet.size);
  const sd = state.structuresData;

  // Clamp each kind's authored level against its catalogue maximum so a
  // future Balance.xlsx retune doesn't accidentally produce out-of-range
  // levels. Caps come from the structures catalogue (per-kind `maxLevel`).
  const clampLevel = (kind: StructureKind, level: number): number => {
    const cap = sd.byKind[kind]?.maxLevel ?? 1;
    return Math.max(0, Math.min(cap, Math.floor(level)));
  };

  const queue: Array<{ kind: StructureKind; level: number }> = [
    { kind: 'underground-bunkers', level: clampLevel('underground-bunkers', biomeDef.bunkers) },
    {
      kind: 'kinetic-surface-battery',
      level: clampLevel('kinetic-surface-battery', biomeDef.surfaceBatteries),
    },
    { kind: 'embassy', level: clampLevel('embassy', biomeDef.embassy) },
  ];

  for (const { kind, level } of queue) {
    if (level <= 0) continue;
    if (planet.structureIds.length >= slotCap) return;
    const hull = maxHullForLevel(sd, kind, level);
    const structure: Structure = {
      id: allocEntityId(state),
      planetId: planet.id,
      kind,
      level,
      buildTicksRemaining: 0, // pre-built, no in-flight construction
      queuedAt: state.tick,
      currentHull: hull,
      maxHull: hull,
      weaponCooldownTicks: 0,
    };
    state.structures.set(structure.id, structure);
    planet.structureIds.push(structure.id);
  }
}

/**
 * Per-race auto-researched Settlement techs at game start (MFix3.1).
 *
 * Derived from `docs/Habitability.xlsx` Race Affinity sheet via the rule:
 * a race auto-researches Settlement: <Biome> for every biome where its
 * RaceAffinity ≥ 0, EXCLUDING the three hostile biomes (Toxic, Volcanic,
 * Radioactive — locked in PLANETS §2.2 as "mid/late-game research"
 * regardless of affinity). Gaia is universally colonisable per §2.2 and
 * is handled in the colonise command directly — no Settlement: Gaia tech
 * exists.
 *
 * Any race CAN research a Settlement tech that is not in its starter
 * set; the table here only governs which are pre-researched at game
 * start. Hostile biomes are paid research for every race.
 */
const AUTO_SETTLEMENT_BY_RACE: Readonly<Record<RaceId, readonly string[]>> = Object.freeze({
  'RACE-AA': ['Settlement: Desert', 'Settlement: Tropical', 'Settlement: Oceanic'],
  'RACE-BB': [
    'Settlement: Barren',
    'Settlement: Desert',
    'Settlement: Tropical',
    'Settlement: Oceanic',
    'Settlement: Frozen',
  ],
  'RACE-CC': ['Settlement: Tropical', 'Settlement: Oceanic', 'Settlement: Frozen'],
  // v2-deferred races have empty auto-Settlement sets; they're not playable in v1.
  'RACE-DD': [],
  'RACE-EE': [],
  // v3M21.1 — synthetic neutral empire doesn't research Settlement
  // techs; the structures + pop seeded by scenario-gen exist as
  // pre-built state, not as the result of any research path.
  Neutral: [],
});

/**
 * Build the initial `researchedTechs` set for a new empire: every tech
 * that's zero-cost with no prereqs and a matching (or 'All') race gate,
 * plus the per-race auto-Settlement set per `AUTO_SETTLEMENT_BY_RACE`.
 * The cost-0 / no-prereq filter captures every Mk 1 baseline in the
 * tree so the research panel starts in a consistent state (all Mk 1s
 * ✓); the auto-Settlement set is layered on top because Settlement:
 * <Biome> techs are non-zero cost (paid research path stays open for
 * hostile biomes and for non-affinity biomes the race may want to
 * expand into).
 *
 * MFix7.1 — the `CLOAKING_TECH_ID` playtest-UX carryover from M8 is
 * removed. Cloaking now requires explicit research like any other
 * tech.
 */
function collectBaselineTechs(state: GameState, raceId: RaceId): Set<string> {
  const out = new Set<string>();
  for (const tech of state.techs.values()) {
    if (tech.researchCost !== 0) continue;
    if (tech.prerequisites.length !== 0) continue;
    if (tech.race !== 'All' && tech.race !== raceId) continue;
    out.add(tech.techName);
  }
  for (const techName of AUTO_SETTLEMENT_BY_RACE[raceId] ?? []) {
    out.add(techName);
  }
  return out;
}

const ORBIT_RADII = [0.14, 0.22, 0.3, 0.38] as const;

function makePlanet(
  state: GameState,
  sys: StarSystem,
  ownerEmpireId: number | null,
  /**
   * v3M20 — flavour name picked by the caller from `data.names.planets`
   * via `pickFromPool`. The caller owns the per-galaxy used-name Set
   * so name uniqueness stays consistent across both the standard
   * planet-gen loop and the homeworld assignment loop.
   */
  name: string,
  biome: Planet['biome'] = 'barren',
  size: Planet['size'] = 15, // HOME_PLANET_SIZE default; v3M10.1 moved to state.balanceData.economy.homePlanetSize
  startingPop = 0,
): Planet {
  const id = allocEntityId(state);
  const slot = sys.planetIds.length % ORBIT_RADII.length;
  const radius = ORBIT_RADII[slot]!;
  const angle = nextFloat(state.rng) * Math.PI * 2;
  const planet: Planet = {
    id,
    systemId: sys.id,
    name,
    biome,
    size,
    // MFix1.4 — populationCap = size × POP_CAP_PER_SIZE (PLANETS §2.1).
    populationCap: size * POP_CAP_PER_SIZE,
    currentPopulation: startingPop,
    ownerEmpireId,
    systemX: 0.5 + Math.cos(angle) * radius,
    systemY: 0.5 + Math.sin(angle) * radius,
    structureIds: [],
    shipBuildQueue: [],
    terraforming: null,
    loyalty: 100,
    bombardKillsAccumulator: 0,
    bombardPopBaseline: null,
    lastBombardedTick: null,
    lastBombardingEmpireId: null,
    // v3M21.1 — diplomacy fields default to "no diplomatic state".
    // Influence pools accumulate as empires spend Diplomatic via
    // setDiplomaticFocus; outcome flags set on outcome invocation.
    influence: new Map(),
    federatedWith: null,
    tradingWith: [],
    vassalOf: null,
  };
  state.planets.set(planet.id, planet);
  return planet;
}

/**
 * Per-race starting loadout. Each empire spawns this ordered list of chassis
 * at home-system start. The mix is intentionally varied — a scout for cheap
 * sensors, a small combatant, and a larger combatant — so the detection /
 * stealth / movement sub-systems are exercised with different visionRange,
 * scanRange, detectionBase and stealthBase values straight out of the new
 * game flow. Add or remove entries here to reshape the starter army without
 * touching spawn math below.
 */
const STARTING_FLEET_BY_RACE: Readonly<Partial<Record<RaceId, readonly string[]>>> = {
  // Constructor chassis (Engineer / Constructor / Mechanic) is included in
  // the starter fleet so M14 space-structure builds are testable without
  // first cycling a ship through the M10 Shipyard. Matches the M8 pattern
  // of pre-seeding Cloaking tech for C-key playtest.
  'RACE-AA': ['Scout', 'Corvette', 'Cruiser', 'Engineer'],
  'RACE-BB': ['Ranger', 'Fighter', 'Praetorian', 'Constructor'],
  'RACE-CC': ['Probe', 'Infiltrator', 'Warship', 'Mechanic'],
};

function spawnStartingShips(
  state: GameState,
  empire: Empire,
  systemId: number,
  data: LoadedGameData,
): void {
  const fleet = STARTING_FLEET_BY_RACE[empire.raceId] ?? [];
  const startRingRadius = 0.09;
  // Space ships evenly around the home planet. Dividing by the slot count
  // (not the fleet length) keeps the ring geometry stable if a spawn is
  // skipped due to a missing template — ships still land at fixed angles
  // instead of bunching up.
  const slotCount = Math.max(fleet.length, 1);
  let spawnIdx = 0;

  const spawn = (chassis: string): void => {
    const key = `${empire.raceId}:${chassis}`;
    const tmpl = data.shipTemplates.get(key);
    if (!tmpl) return;
    const angle = (spawnIdx * (Math.PI * 2)) / slotCount + Math.PI / 6;
    spawnIdx++;
    const ship: ShipInstance = {
      id: allocEntityId(state),
      chassis,
      ownerEmpireId: empire.id,
      currentHull: effectiveMaxHull(state, empire, tmpl),
      currentShields: effectiveMaxShields(state, empire, tmpl),
      currentEnergy: tmpl.energyCapacity ?? 0,
      disabledTicks: 0,
      cloaked: false,
      systemId,
      sx: 0.5 + Math.cos(angle) * startRingRadius,
      sy: 0.5 + Math.sin(angle) * startRingRadius,
      moveOrder: null,
      jumpOriginSystemId: null,
      jumpTargetSystemId: null,
      jumpTargetJumpPointId: null,
      jumpTargetGateId: null,
      pendingWarpGateLinkId: null,
      jumpTicksRemaining: 0,
      jumpDurationTotal: 0,
      lastFiredTick: null,
      lastFiredTargetShipId: null,
      pendingJumpPointId: null,
      pendingColoniseTargetId: null,
      constructionOrder: null,
      bombardOrder: null,
      weaponSlots: initWeaponSlots(tmpl.weaponLoadout, state.weapons, empire.researchedTechs),
      // v3M22.1 — ship-commands defaults. New ships start in 'idle'
      // mode with no waypoints and no guard target.
      commandKind: 'idle',
      waypoints: [],
      guardTargetKind: null,
      guardTargetId: null,
    };
    state.ships.set(ship.id, ship);
    empire.shipIds.push(ship.id);
  };

  for (const chassis of fleet) spawn(chassis);
}

/**
 * Build fresh `WeaponSlotState`s for a newly-spawned ship, one per entry in
 * its template's weaponLoadout. Slots start on cooldown 0 (ready to fire on
 * the first tick an enemy appears) and with a full magazine — Concussion
 * weapons carry their `magazine` value, everything else carries Infinity so
 * the combat pass never has to branch on ammo tracking for unlimited guns.
 * Exported use-site is scenario spawn and the M10 ship-production command
 * (both initialise ship-instance state from a template).
 */
export function initWeaponSlots(
  loadout: ReadonlyArray<{ weapon: string; count: number }>,
  weapons: ReadonlyMap<string, import('../state/types').WeaponDef>,
  researchedTechs: ReadonlySet<string>,
): WeaponSlotState[] {
  return loadout.map((slot) => {
    const resolved = resolveSlotWeapon(slot.weapon, weapons, researchedTechs);
    const ammo = resolved?.magazine ?? Number.POSITIVE_INFINITY;
    return { cooldownTicks: 0, ammoRemaining: ammo };
  });
}
